import {
  ShieldOff,
  LayoutDashboard,
  DatabaseBackup,
  Gauge,
  Server,
  Radar,
  Archive,
  LifeBuoy,
  Activity,
  Lock,
  CreditCard,
  Package,
  Users,
  BookOpen,
  LayoutTemplate,
  SquareTerminal,
  History,
  LibraryBig,
  type LucideIcon,
} from 'lucide-react';
import { STAFF_ROLES } from '@/lib/landing';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Populated as each module ships; disabled items render as "coming soon". */
  enabled: boolean;
  /** When set, only these roles see the item. Server-side checks are the real guard. */
  roles?: string[];
}

export interface NavSection {
  title: string;
  items: NavItem[];
  roles?: string[];
}

const STAFF = STAFF_ROLES;
/** The platform's own health is the operator's job, not a support engineer's. */
const OPERATORS = ['super_admin', 'admin'];
/** Everyone who is not staff. Their dashboard is their own infrastructure. */
const CUSTOMERS = ['customer'];

/**
 * The portal's information architecture: automation first, then the servers it acts on, then the
 * two things that watch over them — monitoring with its fixes, and backups with their proof.
 */
export const navigation: NavSection[] = [
  {
    title: 'Overview',
    items: [
      // Two entries, one label, mutually exclusive by role — because "Dashboard" means a
      // different page depending on who you are, and *nobody gets both*.
      { label: 'Dashboard', to: '/admin', icon: Gauge, enabled: true, roles: OPERATORS },
      { label: 'Dashboard', to: '/dashboard', icon: LayoutDashboard, enabled: true, roles: CUSTOMERS },
      { label: 'System Status', to: '/system', icon: Activity, enabled: true },
      // Operators only. This is the platform's own backup, not a customer's.
      { label: 'Platform backups', to: '/admin/platform-backups', icon: DatabaseBackup, enabled: true, roles: OPERATORS },
    ],
  },
  // Staff-only, and above the tenant-scoped sections: for anyone who works here, customers and their
  // subscriptions are the job.
  {
    title: 'Administration',
    roles: STAFF,
    items: [
      { label: 'Customers', to: '/admin/customers', icon: Users, enabled: true, roles: STAFF },
      { label: 'Support queue', to: '/admin/support', icon: LifeBuoy, enabled: true, roles: STAFF },
      { label: 'Subscriptions', to: '/admin/subscriptions', icon: CreditCard, enabled: true, roles: STAFF },
      { label: 'API tokens', to: '/admin/api-tokens', icon: ShieldOff, enabled: true, roles: STAFF },
      { label: 'Catalog', to: '/admin/catalog', icon: Package, enabled: true, roles: STAFF },
    ],
  },
  {
    title: 'Automation',
    items: [
      { label: 'Playbooks', to: '/automation/playbooks', icon: BookOpen, enabled: true },
      { label: 'Templates', to: '/automation/templates', icon: LayoutTemplate, enabled: true },
      { label: 'Command Runner', to: '/automation/commands', icon: SquareTerminal, enabled: true },
      { label: 'Runs', to: '/automation/runs', icon: History, enabled: true },
    ],
  },
  {
    title: 'Infrastructure',
    items: [
      { label: 'Servers', to: '/servers', icon: Server, enabled: true },
    ],
  },
  {
    title: 'Operations',
    items: [
      { label: 'Monitoring', to: '/monitoring', icon: Radar, enabled: true },
      { label: 'Backups', to: '/backups', icon: Archive, enabled: true },
    ],
  },
  {
    title: 'Account',
    items: [
      { label: 'Billing', to: '/billing', icon: CreditCard, enabled: true },
      { label: 'Security', to: '/settings/security', icon: Lock, enabled: true },
      { label: 'Docs', to: '/help', icon: LibraryBig, enabled: true },
      { label: 'Support', to: '/support', icon: LifeBuoy, enabled: true },
    ],
  },
];

/** Filters the IA down to what the given role may see. */
export function navigationFor(role: string | undefined): NavSection[] {
  return navigation
    .filter((section) => section.roles === undefined || (role !== undefined && section.roles.includes(role)))
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => item.roles === undefined || (role !== undefined && item.roles.includes(role)),
      ),
    }))
    .filter((section) => section.items.length > 0);
}
